// src/components/report/PartBDomainPage.jsx - Fixed for exact reference match
import React from 'react';
import ReactMarkdown from "react-markdown";

const PartBDomainPage = ({ domain, assignment }) => {
    if (!domain || !assignment) return <div className="report-page">Loading Domain Page...</div>;

    const rubricCriteria = ['Awareness', 'Sensitivity', 'Creativity'];

    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            minHeight: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.4',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden'
        }}>
            {/* Orange corner triangles */}
            <div className="absolute top-0 right-0 w-0 h-0"
                style={{
                    borderTop: '60px solid #F97316',
                    borderLeft: '60px solid transparent'
                }}>
            </div>
            <div className="absolute bottom-0 left-0 w-0 h-0"
                style={{
                    borderBottom: '60px solid #F97316',
                    borderRight: '60px solid transparent'
                }}>
            </div>

            {/* PART B Header */}
            <div className="text-center font-bold text-xl text-orange-600 mb-6 mt-2">
                PART B
            </div>

            {/* Centered, short-width headers and two-column box */}
            <div className="w-full flex flex-col items-center">

                {/* DOMAIN HEADER */}
                <div className="w-[680px] max-w-full flex justify-center">
                    <div className="bg-orange-600 text-white py-1 px-4 text-lg font-bold text-center uppercase w-full"
                        style={{ backgroundColor: '#EA580C', borderTopLeftRadius: 3, borderTopRightRadius: 3 }}>
                        DOMAIN {domain.domain_id}: {domain.domain_name?.toUpperCase() || 'Physical development'}
                    </div>
                </div>

                {/* Curricular Goals and Competencies - Two-column layout */}
                <div className="w-[680px] max-w-full flex gap-0 border border-orange-400 border-t-0"
                    style={{ borderRadius: 3 }}>
                    {/* Curricular Goals column */}
                    <div className="w-1/2 border-r border-orange-400 bg-[#fde9d9]">
                        <div className="font-bold text-orange-800 px-3 pt-3 pb-1">
                            <div className="font-bold text-sm">Curricular Goals:</div>
                            <div className="font-normal text-xs italic">(Can choose one or more)</div>
                        </div>
                        <div className="px-3 pb-3 pt-1">
                            <ul className="list-disc list-inside space-y-1">
                                {assignment.curricular_goals?.map((goal, i) => (
                                    <li key={i} className="text-sm leading-tight">
                                        {goal === 'CG-1' ? 'Children develop habits that keep them healthy and safe.' :
                                            goal === 'CG-2' ? 'Children develop sharpness in sensorial perceptions.' :
                                                goal === 'CG-3' ? 'Children develop a fit and flexible body.' : goal}
                                    </li>
                                )) || [
                                    'Children develop habits that keep them healthy and safe.',
                                    'Children develop sharpness in sensorial perceptions.',
                                    'Children develop a fit and flexible body.'
                                ].map((goal, i) => (
                                    <li key={i} className="text-sm leading-tight">{goal}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    {/* Competencies column */}
                    <div className="w-1/2 bg-[#FDE9D9]">
                        <div className="font-bold text-orange-800 px-3 pt-3 pb-1">
                            <div className="font-bold text-sm">Competency/Competencies</div>
                            <div className="font-normal text-xs italic">(Can choose one or more)</div>
                        </div>
                        <div className="px-3 pb-3 pt-1">
                            <ul className="list-disc list-inside space-y-1">
                                {assignment.competencies?.map((comp, i) => (
                                    <li key={i} className="text-sm leading-tight">{comp}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* ACTIVITY HEADER */}
                <div className="w-[680px] max-w-full flex justify-center">
                    <div className="bg-orange-600 text-white py-1 px-4 text-center font-bold text-base w-full"
                        style={{ backgroundColor: '#EA580C', borderTopLeftRadius: 3, borderTopRightRadius: 3 }}>
                        ACTIVITY
                    </div>
                </div>

                <div
                    className="border-2 border-orange-300 p-3 bg-white"
                    style={{
                        minHeight: '120px',
                        maxHeight: '140px',
                        overflowY: 'auto',      // <-- enables vertical scroll if needed
                        overflowX: 'hidden'     // <-- disables horizontal scroll
                    }}
                >
                    <div className="text-sm leading-relaxed">
                        {assignment.activity ? (
                            <div className="prose prose-sm max-w-none">
                                <ReactMarkdown
                                    components={{
                                        h3: ({ children }) => <div className="font-bold mb-2 text-sm">{children}</div>,
                                        h4: ({ children }) => <div className="font-bold mb-1 text-sm">{children}</div>,
                                        p: ({ children }) => <p className="mb-2 text-sm">{children}</p>,
                                        ul: ({ children }) => <ul
                                            className="list-disc list-inside mb-2 text-sm">{children}</ul>,
                                        ol: ({ children }) => <ol
                                            className="list-decimal list-inside mb-2 text-sm">{children}</ol>,
                                        li: ({ children }) => <li className="mb-1 text-sm">{children}</li>,
                                        strong: ({ children }) => <strong
                                            className="font-bold text-sm">{children}</strong>
                                    }}
                                >
                                    {assignment.activity}
                                </ReactMarkdown>
                            </div>
                        ) : ''}
                    </div>
                </div>


                {/* ASSESSMENT QUESTIONS HEADER */}
                <div className="w-[680px] max-w-full flex justify-center">
                    <div className="bg-orange-600 text-white py-1 px-4 text-center font-bold text-base w-full"
                        style={{ backgroundColor: '#EA580C', borderTopLeftRadius: 3, borderTopRightRadius: 3 }}>
                        ASSESSMENT QUESTIONS
                    </div>
                </div>

                {/* Assessment Questions Section */}
                <div
                    className="w-[680px] max-w-full border border-orange-300 border-t-0 bg-white px-4 py-3 mb-8"
                    style={{
                        borderRadius: 3,
                        minHeight: '120px',
                        maxHeight: '140px',
                        overflowY: 'auto',   // Enable vertical scroll if needed
                        overflowX: 'hidden'  // Prevent horizontal scroll
                    }}
                >
                    <ul className="list-decimal list-inside space-y-2">
                        {assignment.assessment_questions?.map((q, i) => (
                            <li key={i} className="text-sm leading-tight">{q}</li>
                        ))}
                    </ul>
                </div>


            </div>

            <div className="mb-4">
                <div className="w-[680px] max-w-full flex justify-center">
                    <div className="bg-orange-600 text-white py-1 px-4 text-center font-bold text-base w-full"
                        style={{ backgroundColor: '#EA580C', marginLeft: '-20mm', marginRight: '-20mm' }}>
                        ASSESSMENT RUBRIC*
                    </div>
                </div>
                <table className="w-full border-collapse">
                    <thead>
                        <tr>
                            <th className="border-2 border-orange-600 p-1 bg-orange-200 w-24"
                                style={{ backgroundColor: '#f89646' }}></th>
                            <th className="border-2 border-orange-600 p-1 text-center font-bold bg-orange-200 text-sm"
                                style={{ backgroundColor: '#f89646' }}>Stream
                            </th>
                            <th className="border-2 border-orange-600 p-1 text-center font-bold bg-orange-200 text-sm"
                                style={{ backgroundColor: '#f89646' }}>Mountain
                            </th>
                            <th className="border-2 border-orange-600 p-1 text-center font-bold bg-orange-200 text-sm"
                                style={{ backgroundColor: '#f89646' }}>Sky
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rubricCriteria.map((criterionName, index) => {
                            const rubricItem = assignment.assessment_rubric?.find(r => r.criterion === criterionName);
                            return (
                                <tr key={criterionName}>
                                    <td className="border-2 border-orange-600 p-2 font-bold bg-orange-100 text-sm"
                                        style={{ backgroundColor: '#fde9d9' }}>{criterionName}</td>
                                    <td className="border-2 border-orange-600 p-2 text-center text-xs bg-white"
                                        style={{ minHeight: '40px', verticalAlign: 'middle' }}>
                                        {rubricItem?.stream || ''}
                                    </td>
                                    <td className="border-2 border-orange-600 p-2 text-center text-xs bg-white"
                                        style={{ minHeight: '40px', verticalAlign: 'middle' }}>
                                        {rubricItem?.mountain || ''}
                                    </td>
                                    <td className="border-2 border-orange-600 p-2 text-center text-xs bg-white"
                                        style={{ minHeight: '40px', verticalAlign: 'middle' }}>
                                        {rubricItem?.sky || ''}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>


            {/* Assessment Rubric Section */}


            <p className="text-xs ">
                * <b>Note:</b> Circle the relevant performance level based on the individual student's performance for
                each
                ability for this activity.
            </p>

            <div className="border-b-2 mt-2 border-[#fbd3b1] w-full mb-1" />
        </div>
    );
};

export default PartBDomainPage;